import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { FORM_FIELDS as f } from '../config';

import useRegions from '../utils/useRegions';
import useTranslatedOptions from './useTranslatedOptions';
import TextInput from '../components/form-components/text-input';
import CheckboxInput from '../components/form-components/checkbox-input';
import DropdownInput from '../components/form-components/dropdown-input';
import CheckboxGroupInput from '../components/form-components/checkbox-group-input';
import RecaptchaInputWrapper from '../components/form-components/recaptcha-input-wrapper';
import FilepickerInputWrapper from '../components/form-components/filepicker-input-wrapper';

const useFields = () => {
  const { t } = useTranslation();
  const REQUIRED_TKEY = t('Mandatory_Error');
  const EMAIL_FIELD_TKEY = t('Email_Field');
  const FIELD_LENGTH_40_TKEY = t('FieldLength_40');
  const FIELD_LENGTH_50_TKEY = t('FieldLength_50');
  const FIELD_LENGTH_80_TKEY = t('FieldLength_80');
  const FIELD_LENGTH_255_TKEY = t('FieldLength_255');
  const { getTcsLink, getPrivacyPolicyLink } = useRegions();
  const {
    getCountries,
    getMainGenre,
    getProfileStatement,
    getPhotoSkills,
    getVideoSkills,
    getPrintStatement,
    getAgeStatement,
    getProductStatement,
    getProfessionStatement,
  } = useTranslatedOptions();

  const FirstName = {
    name: f.FIRST_NAME,
    initial: '',
    tkey: 'FirstName',
    mandatory: true,
    Component: TextInput,
    validation: Yup.string().max(40, FIELD_LENGTH_40_TKEY).required(REQUIRED_TKEY),
  };
  const LastName = {
    name: f.LAST_NAME,
    initial: '',
    tkey: 'LastName',
    mandatory: true,
    Component: TextInput,
    validation: Yup.string().max(80, FIELD_LENGTH_80_TKEY).required(REQUIRED_TKEY),
  };
  const EmailAddress = {
    name: f.EMAIL_ADDRESS,
    initial: '',
    tkey: 'EmailAddress',
    mandatory: true,
    Component: TextInput,
    validation: Yup.string()
      .email(EMAIL_FIELD_TKEY)
      .max(255, FIELD_LENGTH_255_TKEY)
      .required(REQUIRED_TKEY),
  };
  const CountryOfResidence = {
    name: f.COUNTRY_OF_RESIDENCE,
    initial: '',
    tkey: 'Country_Selector',
    mandatory: true,
    options: {
      variants: getCountries(),
      addOther: false,
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const ProfileStatement = {
    name: f.PROFILE_STATEMENT,
    initial: '',
    tkey: 'ProfileStatement_Q',
    mandatory: true,
    options: {
      variants: getProfileStatement(),
      addOther: false,
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const PrintStatement = {
    name: f.PRINT_STATEMENT,
    initial: '',
    tkey: 'PrintStatement_Q',
    mandatory: true,
    options: {
      variants: getPrintStatement(),
      addOther: false,
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const MainGenre = {
    name: f.MAIN_GENRE,
    initial: '',
    tkey: 'MainGenre_Q',
    mandatory: true,
    options: { variants: getMainGenre() },
    Component: CheckboxGroupInput,
    validation: Yup.array()
      .of(Yup.string().oneOf(getMainGenre.keys))
      .required(REQUIRED_TKEY)
      .min(1, REQUIRED_TKEY)
      .max(3, REQUIRED_TKEY),
  };
  const PhotoSkills = {
    name: f.PHOTO_SKILLS,
    initial: '',
    tkey: 'PhotographySkill_Q',
    mandatory: true,
    options: {
      variants: getPhotoSkills(),
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const VideoSkills = {
    name: f.VIDEO_SKILLS,
    initial: '',
    tkey: 'VideoSkill_Q',
    mandatory: true,
    options: {
      variants: getVideoSkills(),
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const PhotoCaption = {
    name: f.PHOTO_CAPTION,
    initial: '',
    tkey: 'PhotoCaption_Q',
    mandatory: true,
    Component: TextInput,
    validation: Yup.string().max(50, FIELD_LENGTH_50_TKEY).required(REQUIRED_TKEY),
  };
  const Media = {
    name: f.MEDIA,
    tkey: 'File_Uploader_T',
    initial: [],
    options: { maxFiles: 1, format: ['image/jpeg', 'image/png', 'image/tif', '.pdf'] },
    mandatory: true,
    Component: FilepickerInputWrapper,
    validation: Yup.array()
      .of(Yup.string().required(REQUIRED_TKEY))
      .required(REQUIRED_TKEY)
      .min(1, REQUIRED_TKEY),
  };
  const DataPrivacyPolicy = {
    name: f.DATA_PRIVACY_POLICY,
    tkey: 'DataPrivacyPolicy',
    initial: false,
    options: { link: getPrivacyPolicyLink() },
    mandatory: true,
    Component: CheckboxInput,
    validation: Yup.bool().oneOf([true], REQUIRED_TKEY).required(REQUIRED_TKEY),
  };
  const PhotogalleryConsent = {
    name: f.PHOTOGALLERY_CONSENT,
    tkey: 'GalleryConsent',
    initial: false,
    mandatory: true,
    Component: CheckboxInput,
    validation: Yup.bool().oneOf([true], REQUIRED_TKEY).required(REQUIRED_TKEY),
  };
  const MarketingConsent = {
    name: f.MARKETING_CONSENT,
    tkey: 'MarketingConsent',
    initial: false,
    mandatory: false,
    Component: CheckboxInput,
    validation: Yup.bool(),
  };
  const TermsAndConditions = {
    name: f.TERMS_AND_CONDITIONS,
    tkey: 'TCsAccepted',
    initial: false,
    options: { link: getTcsLink() },
    mandatory: true,
    Component: CheckboxInput,
    validation: Yup.bool().oneOf([true], REQUIRED_TKEY).required(REQUIRED_TKEY),
  };
  const TransferConsent = {
    name: f.TRANSFER_CONSENT,
    tkey: 'TransferConsent',
    initial: false,
    mandatory: true,
    Component: CheckboxInput,
    validation: Yup.bool().oneOf([true], REQUIRED_TKEY).required(REQUIRED_TKEY),
  };
  const CAPTCHA = {
    name: f.CAPTCHA,
    initial: '',
    tkey: '',
    mandatory: false,
    Component: RecaptchaInputWrapper,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const AgeStatement = {
    name: f.AGE_STATEMENT,
    initial: '',
    tkey: 'Age_Category_Q',
    mandatory: true,
    options: {
      variants: getAgeStatement(),
      addOther: false,
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const ProfessionStatement = {
    name: f.PROFESSION_STATEMENT,
    initial: '',
    tkey: 'Profession_Q',
    mandatory: true,
    options: {
      variants: getProfessionStatement(),
      addOther: true,
      tkeys: ['Profession_Other'],
    },
    Component: DropdownInput,
    validation: Yup.string().required(REQUIRED_TKEY),
  };
  const ProductStatement = {
    name: f.PRODUCT_STATEMENT,
    initial: '',
    tkey: 'Product_Q',
    mandatory: true,
    options: {
      variants: getProductStatement(),
      maxSelection: 6,
    },
    Component: CheckboxGroupInput,
    validation: Yup.array()
      .of(Yup.string().oneOf(getProductStatement.keys))
      .required(REQUIRED_TKEY)
      .min(1, REQUIRED_TKEY)
      .max(6, REQUIRED_TKEY),
  };

  return {
    FirstName,
    LastName,
    EmailAddress,
    CountryOfResidence,
    MainGenre,
    ProfileStatement,
    PhotoSkills,
    VideoSkills,
    PrintStatement,
    PhotoCaption,
    Media,
    DataPrivacyPolicy,
    PhotogalleryConsent,
    MarketingConsent,
    TermsAndConditions,
    TransferConsent,
    CAPTCHA,
    AgeStatement,
    ProfessionStatement,
    ProductStatement,
  };
};

export default useFields;
